import { useCallback, useEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';

interface ZoneRoute {
  name: string;
  icon: string;
  color: string;
  center: [number, number];
  zoom: number;
  desc: string;
  streets: string[];
  coords: [number, number][];
  labels: { pos: [number, number]; text: string }[];
}

const zoneRoutes: ZoneRoute[] = [
  {
    name: 'Downtown Tampa',
    icon: 'ri-road-map-line',
    color: '#00d4ff',
    center: [27.9480, -82.4570],
    zoom: 15,
    desc: 'High foot traffic, events, nightlife',
    streets: ['Tampa St', 'Franklin St', 'Ashley Dr', 'Kennedy Blvd', 'Florida Ave'],
    coords: [
      [27.9430, -82.4584],
      [27.9480, -82.4584],
      [27.9520, -82.4584],
      [27.9520, -82.4622],
      [27.9470, -82.4622],
      [27.9447, -82.4622],
      [27.9447, -82.4540],
      [27.9447, -82.4510],
      [27.9480, -82.4510],
      [27.9520, -82.4510],
      [27.9520, -82.4566],
      [27.9520, -82.4584],
      [27.9430, -82.4584],
    ],
    labels: [
      { pos: [27.9520, -82.4600], text: 'ASHLEY DR' },
      { pos: [27.9447, -82.4565], text: 'KENNEDY BLVD' },
      { pos: [27.9490, -82.4510], text: 'FLORIDA AVE' },
      { pos: [27.9520, -82.4584], text: 'FRANKLIN ST' },
    ],
  },
  {
    name: 'Ybor City',
    icon: 'ri-building-2-line',
    color: '#39ff14',
    center: [27.9580, -82.4380],
    zoom: 15,
    desc: 'Entertainment district, weekends & nights',
    streets: ['7th Ave (La Séptima)', '22nd St', '8th Ave', 'Nebraska Ave'],
    coords: [
      [27.9606, -82.4450],
      [27.9606, -82.4389],
      [27.9606, -82.4310],
      [27.9560, -82.4310],
      [27.9540, -82.4310],
      [27.9540, -82.4389],
      [27.9540, -82.4450],
      [27.9606, -82.4450],
    ],
    labels: [
      { pos: [27.9606, -82.4380], text: '7TH AVE (LA SÉPTIMA)' },
      { pos: [27.9570, -82.4310], text: '22ND ST' },
      { pos: [27.9540, -82.4400], text: '8TH AVE' },
    ],
  },
  {
    name: 'Westshore / Airport',
    icon: 'ri-store-2-line',
    color: '#b026ff',
    center: [27.9580, -82.5190],
    zoom: 13,
    desc: 'Business corridor, hotels, commuters',
    streets: ['Kennedy Blvd', 'Westshore Blvd', 'Boy Scout Blvd', 'Henderson Blvd'],
    coords: [
      [27.9447, -82.5090],
      [27.9447, -82.5200],
      [27.9447, -82.5268],
      [27.9600, -82.5268],
      [27.9730, -82.5268],
      [27.9730, -82.5150],
      [27.9730, -82.5090],
      [27.9600, -82.5090],
      [27.9447, -82.5090],
    ],
    labels: [
      { pos: [27.9447, -82.5180], text: 'KENNEDY BLVD' },
      { pos: [27.9600, -82.5268], text: 'WESTSHORE BLVD' },
      { pos: [27.9730, -82.5210], text: 'BOY SCOUT BLVD' },
    ],
  },
  {
    name: 'South Tampa',
    icon: 'ri-sun-line',
    color: '#ff073a',
    center: [27.9250, -82.4960],
    zoom: 12,
    desc: 'Upscale neighborhoods, restaurants',
    streets: ['Dale Mabry Hwy', 'Gandy Blvd', 'Bayshore Blvd', 'Kennedy Blvd'],
    coords: [
      [27.9706, -82.5142], // Dale Mabry & I-275 — START
      [27.9600, -82.5142], // South on Dale Mabry
      [27.9455, -82.5142], // Dale Mabry & Kennedy
      [27.9200, -82.5142], // Continuing south on Dale Mabry
      [27.9050, -82.5142], // South on Dale Mabry
      [27.8940, -82.5142], // Dale Mabry & Gandy — TURN LEFT
      [27.8940, -82.5000], // East on Gandy Blvd
      [27.8940, -82.4822], // Gandy approaching Bayshore
      [27.9050, -82.4822], // South end of Bayshore Blvd — TURN NORTH
      [27.9200, -82.4770], // North on Bayshore
      [27.9350, -82.4655], // North on Bayshore
      [27.9435, -82.4590], // Bayshore near Kennedy (Hyde Park)
      [27.9455, -82.4590], // Kennedy & Bayshore — TURN WEST
      [27.9455, -82.4800], // West on Kennedy Blvd
      [27.9455, -82.5142], // Kennedy & Dale Mabry
      [27.9706, -82.5142], // Back to I-275 — CLOSE LOOP
    ],
    labels: [
      { pos: [27.9200, -82.5142], text: 'DALE MABRY HWY' },
      { pos: [27.8940, -82.5000], text: 'GANDY BLVD' },
      { pos: [27.9250, -82.4770], text: 'BAYSHORE BLVD' },
      { pos: [27.9455, -82.4780], text: 'KENNEDY BLVD' },
    ],
  },
  {
    name: 'Brandon / Riverview',
    icon: 'ri-building-4-line',
    color: '#00d4ff',
    center: [27.9350, -82.3180],
    zoom: 12,
    desc: 'Suburban growth corridor',
    streets: ['Brandon Blvd (US-60)', 'Providence Rd', 'Causeway Blvd', 'Kings Ave'],
    coords: [
      [27.9450, -82.3400],
      [27.9450, -82.3175],
      [27.9450, -82.2950],
      [27.9360, -82.2950],
      [27.9250, -82.2950],
      [27.9250, -82.3175],
      [27.9250, -82.3400],
      [27.9360, -82.3400],
      [27.9450, -82.3400],
    ],
    labels: [
      { pos: [27.9450, -82.3180], text: 'BRANDON BLVD (US-60)' },
      { pos: [27.9250, -82.3180], text: 'CAUSEWAY BLVD' },
      { pos: [27.9360, -82.3400], text: 'KINGS AVE' },
    ],
  },
  {
    name: 'USF / Temple Terrace',
    icon: 'ri-map-pin-range-line',
    color: '#39ff14',
    center: [28.0660, -82.4100],
    zoom: 13,
    desc: 'University area, young demographics',
    streets: ['Fowler Ave', 'Bruce B Downs Blvd', '56th St', 'Fletcher Ave'],
    coords: [
      [28.0620, -82.4300],
      [28.0620, -82.4181],
      [28.0620, -82.4050],
      [28.0620, -82.3900],
      [28.0700, -82.3900],
      [28.0700, -82.4181],
      [28.0700, -82.4300],
      [28.0620, -82.4300],
    ],
    labels: [
      { pos: [28.0620, -82.4100], text: 'FOWLER AVE' },
      { pos: [28.0660, -82.3900], text: 'BRUCE B DOWNS' },
      { pos: [28.0700, -82.4100], text: 'FLETCHER AVE' },
    ],
  },
];

const stats = [
  { value: '50+', label: 'Miles Covered Daily', color: '#00d4ff' },
  { value: '200K+', label: 'Daily Impressions', color: '#39ff14' },
  { value: '15+', label: 'Key Zones', color: '#b026ff' },
];

const ServiceAreaMap = () => {
  const [visible, setVisible] = useState(false);
  const [activeRoute, setActiveRoute] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polylineLayersRef = useRef<any[][]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerLayerGroupsRef = useRef<any[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const applyRouteStyles = useCallback((activeIdx: number) => {
    polylineLayersRef.current.forEach((layers, i) => {
      const [glow, core] = layers;
      if (i === activeIdx) {
        glow.setStyle({ opacity: 0.20, weight: 18 });
        core.setStyle({ opacity: 1, weight: 3, dashArray: '10, 6' });
      } else {
        glow.setStyle({ opacity: 0.03, weight: 12 });
        core.setStyle({ opacity: 0.12, weight: 1.5, dashArray: '' });
      }
    });

    markerLayerGroupsRef.current.forEach((group, i) => {
      if (i === activeIdx) {
        group.eachLayer((layer: any) => layer.setOpacity ? layer.setOpacity(1) : null); // eslint-disable-line @typescript-eslint/no-explicit-any
        group.eachLayer((layer: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          const el = layer.getElement?.();
          if (el) el.style.opacity = '1';
        });
      } else {
        group.eachLayer((layer: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          const el = layer.getElement?.();
          if (el) el.style.opacity = '0';
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const initMap = async () => {
      const L = (await import('leaflet')).default;

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        polylineLayersRef.current = [];
        markerLayerGroupsRef.current = [];
      }

      const map = L.map(mapContainerRef.current!, {
        center: [27.9480, -82.4570],
        zoom: 15,
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
      });
      mapRef.current = map;

      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd',
      }).addTo(map);

      // Draw all zone routes
      zoneRoutes.forEach((zone) => {
        const glowLine = L.polyline(zone.coords, {
          color: zone.color,
          weight: 12,
          opacity: 0.03,
          lineCap: 'round',
          lineJoin: 'round',
        }).addTo(map);

        const coreLine = L.polyline(zone.coords, {
          color: zone.color,
          weight: 1.5,
          opacity: 0.12,
          lineCap: 'round',
          lineJoin: 'round',
        }).addTo(map);

        polylineLayersRef.current.push([glowLine, coreLine]);

        // Marker layer group per zone
        const group = L.layerGroup().addTo(map);

        // Street label markers
        zone.labels.forEach(({ pos, text }) => {
          const icon = L.divIcon({
            html: `<div style="
              background:rgba(0,0,0,0.88);
              border:1px solid ${zone.color};
              color:${zone.color};
              font-family:'Rajdhani',sans-serif;
              font-weight:700;
              font-size:9px;
              letter-spacing:0.10em;
              padding:2px 7px;
              border-radius:3px;
              white-space:nowrap;
              box-shadow:0 0 7px ${zone.color}55;
              text-shadow:0 0 6px ${zone.color};
              opacity:0;
              transition:opacity 0.3s;
            ">${text}</div>`,
            className: '',
            iconAnchor: [0, 8],
          });
          group.addLayer(L.marker(pos as [number, number], { icon }));
        });

        // Start/end dot marker
        const dotIcon = L.divIcon({
          html: `<div style="
            width:12px;height:12px;border-radius:50%;
            background:${zone.color};
            box-shadow:0 0 10px ${zone.color},0 0 20px ${zone.color}80;
            border:2px solid #fff;
            opacity:0;
            transition:opacity 0.3s;
          "></div>`,
          className: '',
          iconAnchor: [6, 6],
        });
        group.addLayer(L.marker(zone.coords[0], { icon: dotIcon }));

        markerLayerGroupsRef.current.push(group);
      });

      L.control.zoom({ position: 'bottomright' }).addTo(map);
      L.control.attribution({ position: 'bottomleft', prefix: false })
        .addAttribution('&copy; <a href="https://carto.com" style="color:#39ff14">CARTO</a> &copy; <a href="https://openstreetmap.org" style="color:#39ff14">OSM</a>')
        .addTo(map);

      // Activate default route (Downtown)
      setTimeout(() => applyRouteStyles(0), 200);
    };

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        polylineLayersRef.current = [];
        markerLayerGroupsRef.current = [];
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRouteClick = useCallback(async (index: number) => {
    setActiveRoute(index);
    const zone = zoneRoutes[index];
    if (mapRef.current) {
      applyRouteStyles(index);
      mapRef.current.flyTo(zone.center, zone.zoom, { animate: true, duration: 1.0 });
    }
  }, [applyRouteStyles]);

  const activeZone = zoneRoutes[activeRoute];

  return (
    <section id="service-area" className="relative py-24 md:py-32 bg-black overflow-hidden" ref={sectionRef}>
      {/* BG Grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(0,212,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none opacity-10"
        style={{ background: 'radial-gradient(circle, #00d4ff 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full pointer-events-none opacity-10"
        style={{ background: 'radial-gradient(circle, #39ff14 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div
            className="inline-block text-xs font-rajdhani font-bold tracking-widest uppercase mb-4 px-3 py-1 rounded-full"
            style={{ color: '#39ff14', border: '1px solid #39ff14', boxShadow: '0 0 10px rgba(57,255,20,0.3)' }}
          >
            Coverage Zone
          </div>
          <h2
            className="font-orbitron font-black text-4xl md:text-5xl lg:text-6xl"
            style={{ color: '#ffffff', textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            WE OWN THE{' '}
            <span style={{ color: '#39ff14', textShadow: '0 0 20px #39ff14' }}>STREETS</span>
          </h2>
          <p className="mt-4 text-gray-400 text-lg font-rajdhani max-w-2xl mx-auto">
            Our LED trucks dominate Tampa&apos;s highest-traffic corridors — putting your brand in front of the people who matter
          </p>
        </div>

        {/* Stats */}
        <div className={`flex flex-wrap justify-center gap-8 mb-14 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-orbitron font-black text-4xl md:text-5xl" style={{ color: stat.color, textShadow: `0 0 20px ${stat.color}` }}>
                {stat.value}
              </div>
              <div className="font-rajdhani text-gray-400 text-sm tracking-widest uppercase mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Map + Routes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Map — 2 columns */}
          <div
            className={`lg:col-span-2 rounded-3xl overflow-hidden transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            style={{ border: `1px solid ${activeZone.color}50`, boxShadow: `0 0 40px ${activeZone.color}15`, transition: 'border-color 0.5s, box-shadow 0.5s' }}
          >
            {/* Top bar */}
            <div
              className="flex items-center gap-2 px-4 py-3"
              style={{ background: 'rgba(0,0,0,0.9)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ff073a' }} />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#39ff14' }} />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#00d4ff' }} />
              <span className="ml-3 font-rajdhani text-xs tracking-widest uppercase" style={{ color: activeZone.color, transition: 'color 0.4s' }}>
                {activeZone.name} — Live Route
              </span>
              <span className="ml-auto flex items-center gap-1">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: activeZone.color }} />
                <span className="font-rajdhani text-xs" style={{ color: activeZone.color }}>TRACKING ACTIVE</span>
              </span>
            </div>

            {/* Streets being covered */}
            <div
              className="flex items-center gap-2 px-4 py-2 overflow-x-auto"
              style={{ background: 'rgba(0,0,0,0.7)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
            >
              <span className="font-rajdhani text-xs text-gray-500 whitespace-nowrap shrink-0">Streets:</span>
              {activeZone.streets.map((street, i) => (
                <span key={street} className="flex items-center gap-1">
                  {i > 0 && <i className="ri-arrow-right-s-line text-gray-700 text-xs" />}
                  <span
                    className="font-rajdhani font-bold text-xs whitespace-nowrap px-2 py-0.5 rounded"
                    style={{ color: activeZone.color, background: `${activeZone.color}12`, border: `1px solid ${activeZone.color}30` }}
                  >
                    {street}
                  </span>
                </span>
              ))}
            </div>

            {/* Leaflet map */}
            <div ref={mapContainerRef} style={{ height: '420px', width: '100%' }} />
          </div>

          {/* Routes sidebar */}
          <div className={`flex flex-col gap-3 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div
              className="px-4 py-3 rounded-2xl mb-1"
              style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}
            >
              <h3 className="font-rajdhani font-bold text-lg uppercase tracking-widest" style={{ color: '#00d4ff' }}>
                Active Routes
              </h3>
              <p className="text-gray-500 text-xs mt-1 font-rajdhani">Click any zone — route draws on map</p>
            </div>

            {zoneRoutes.map((zone, i) => (
              <button
                key={zone.name}
                type="button"
                onClick={() => handleRouteClick(i)}
                className={`group flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 w-full text-left ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{
                  background: activeRoute === i ? `${zone.color}12` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${activeRoute === i ? zone.color + '60' : zone.color + '20'}`,
                  boxShadow: activeRoute === i ? `0 0 20px ${zone.color}25` : 'none',
                  transitionDelay: `${400 + i * 70}ms`,
                }}
                onMouseEnter={(e) => {
                  if (activeRoute !== i) {
                    (e.currentTarget as HTMLButtonElement).style.background = `${zone.color}10`;
                    (e.currentTarget as HTMLButtonElement).style.borderColor = `${zone.color}45`;
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 16px ${zone.color}18`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeRoute !== i) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.03)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = `${zone.color}20`;
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
                  }
                }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
                  style={{
                    backgroundColor: `${zone.color}15`,
                    boxShadow: activeRoute === i ? `0 0 14px ${zone.color}60` : `0 0 8px ${zone.color}25`,
                  }}
                >
                  <i className={`${zone.icon} text-lg`} style={{ color: zone.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-rajdhani font-bold text-sm" style={{ color: zone.color }}>{zone.name}</div>
                  <div className="text-gray-500 text-xs mt-0.5 font-rajdhani">{zone.desc}</div>
                </div>
                <div className="shrink-0 flex items-center mt-1">
                  {activeRoute === i ? (
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: zone.color }} />
                  ) : (
                    <i className="ri-arrow-right-s-line text-gray-600 group-hover:text-gray-400 transition-colors" />
                  )}
                </div>
              </button>
            ))}

            <a
              href="#contact"
              className="mt-2 w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-rajdhani font-bold tracking-widest uppercase text-sm text-black transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ backgroundColor: '#00d4ff', boxShadow: '0 0 25px rgba(0,212,255,0.4)' }}
            >
              <i className="ri-map-pin-add-line" />
              Book a Route Today
            </a>
          </div>
        </div>

        {/* Active route strip */}
        <div
          className={`mt-10 rounded-2xl px-6 py-4 transition-all duration-500 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ background: `${activeZone.color}06`, border: `1px solid ${activeZone.color}20` }}
        >
          <div className="flex flex-wrap items-center justify-center gap-2 font-rajdhani text-sm">
            <span className="text-gray-500 text-xs tracking-widest uppercase mr-1">Active zone:</span>
            <span className="font-bold" style={{ color: activeZone.color }}>{activeZone.name}</span>
            <span className="text-gray-600 mx-1">·</span>
            {activeZone.streets.map((s, i) => (
              <span key={s} className="flex items-center gap-1">
                {i > 0 && <i className="ri-arrow-right-s-line text-gray-700" />}
                <span style={{ color: activeZone.color }}>{s}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Tagline */}
        <div className={`text-center mt-10 transition-all duration-700 delay-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-orbitron text-xl md:text-2xl font-bold" style={{ color: '#ffffff', textShadow: '0 0 20px rgba(0,212,255,0.4)' }}>
            &quot;If you&apos;re in Tampa —{' '}
            <span style={{ color: '#39ff14', textShadow: '0 0 15px #39ff14' }}>we&apos;ll find your customers.&quot;</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
